"use client"
import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useMemo,
    useCallback
} from "react"
import { CustomerInfo } from "./types"

const defaultCustomerInfo: CustomerInfo = {
    billingAddress: {
        country: "",
        firstName: "",
        lastName: "",
        address1: "",
        phoneNumber: "",
        zipCode: "",
        email: "",
        city: ""
    },
    shippingAddress: {
        country: "",
        firstName: "",
        lastName: "",
        address1: "",
        zipCode: "",
        city: "",
        state: ""
    },
    differentShippingAddress: false,
    newsletter: false
}

interface CheckoutContextType {
    customerInfo: CustomerInfo
    updateCustomerInfo: (updates: Partial<CustomerInfo>) => void
    loading: boolean
}

const CheckoutContext = createContext<CheckoutContextType | null>(null)

interface CheckoutProviderProps {
    children: ReactNode
}

export const CheckoutProvider = ({ children }: CheckoutProviderProps) => {
    const [customerInfo, setCustomerInfo] = useState<CustomerInfo>(defaultCustomerInfo)
    const [loading, setLoading] = useState<boolean>(false)

    const updateCustomerInfo = useCallback((updates: Partial<CustomerInfo>) => {
        setLoading(true)
        setCustomerInfo(prevInfo => ({ ...prevInfo, ...updates }))
        setLoading(false)
    }, [])

    const contextValue = useMemo(
        () => ({
            customerInfo,
            updateCustomerInfo,
            loading
        }),
        [customerInfo, loading, updateCustomerInfo]
    )

    return (
        <CheckoutContext.Provider value={contextValue}>
            {children}
        </CheckoutContext.Provider>
    )
}

export const useCheckoutForm = () => {
    const context = useContext(CheckoutContext)
    if (!context) {
        throw new Error("useCheckoutForm must be used within a CheckoutProvider")
    }
    return context
}
